import { useState, useEffect } from "react";
import { getReportsSummary, exportExpenses, getCategories, getUsers } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { format, subMonths } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Download,
  CalendarIcon,
  TrendingUp,
  PieChart,
  BarChart3,
  FileSpreadsheet,
  Loader2,
  BookOpen,
  Building2,
  Users,
  X
} from "lucide-react";
import { cn } from "../lib/utils";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart as RechartsPie, 
  Pie, 
  Cell,
  LineChart,
  Line,
  Legend
} from "recharts";

const COLORS = ["hsl(222, 47%, 11%)", "hsl(142, 71%, 45%)", "hsl(210, 40%, 60%)", "hsl(0, 84.2%, 60.2%)", "hsl(38, 92%, 50%)", "hsl(280, 65%, 60%)", "hsl(180, 50%, 45%)", "hsl(60, 70%, 50%)"];

const statusLabels = {
  draft: "Brouillon",
  submitted: "En attente",
  approved: "Approuvé",
  rejected: "Rejeté",
  reimbursed: "Remboursé"
};

export default function Reports() {
  const { user, hasRole } = useAuth();
  const isManager = hasRole(["admin", "manager", "accountant"]);

  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [dateFrom, setDateFrom] = useState(subMonths(new Date(), 6));
  const [dateTo, setDateTo] = useState(new Date());
  const [exportStatus, setExportStatus] = useState("all");
  const [selectedUser, setSelectedUser] = useState("all");

  const hasMonthFilter = filterMonth && filterYear;

  useEffect(() => {
    fetchData();
  }, [filterMonth, filterYear, dateFrom, dateTo, selectedUser]);

  useEffect(() => {
    if (isManager) {
      getUsers().then(res => setUsers(res.data)).catch(() => {});
    }
  }, [isManager]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (hasMonthFilter) {
        params.expense_month = `${filterYear}-${filterMonth}`;
      } else {
        if (dateFrom) params.date_from = format(dateFrom, "yyyy-MM-dd");
        if (dateTo) params.date_to = format(dateTo, "yyyy-MM-dd");
      }
      if (selectedUser !== "all") {
        params.user_id = selectedUser;
      }
      const [summaryRes, categoriesRes] = await Promise.all([
        getReportsSummary(params),
        getCategories()
      ]);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (formatType) => {
    setExporting(true);
    try {
      const params = { format: formatType };
      if (hasMonthFilter) {
        params.expense_month = `${filterYear}-${filterMonth}`;
      } else {
        if (dateFrom) params.date_from = format(dateFrom, "yyyy-MM-dd");
        if (dateTo) params.date_to = format(dateTo, "yyyy-MM-dd");
      }
      if (exportStatus !== "all") {
        params.status = exportStatus;
      }
      if (selectedUser !== "all") {
        params.user_id = selectedUser;
      }

      const response = await exportExpenses(params);

      // Détermine le type MIME et l'extension selon le format
      const mimeTypes = {
        csv:   'text/csv',
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        sage:  'text/plain',
        cegid: 'text/csv'
      };
      const extensions = { csv: 'csv', excel: 'xlsx', sage: 'txt', cegid: 'csv' };
      const fileNames = { sage: 'sage_ndf', cegid: 'cegid_ndf' };
      const exportLabels = { sage: 'SAGE (FEC)', cegid: 'CEGID' };

      const blob = new Blob([response.data], { type: mimeTypes[formatType] || 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileNames[formatType] || 'expenses'}_${format(new Date(), 'yyyyMMdd')}.${extensions[formatType] || 'txt'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`Export ${exportLabels[formatType] || formatType.toUpperCase()} téléchargé`);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Prepare chart data
  const monthlyData = summary?.by_month 
    ? Object.entries(summary.by_month).map(([month, amount]) => ({
        name: month,
        montant: amount
      }))
    : [];

  const categoryData = summary?.by_category && categories.length > 0
    ? Object.entries(summary.by_category).map(([catId, amount]) => {
        const cat = categories.find(c => c.id === catId);
        return {
          name: cat?.name || "Autre",
          value: amount
        };
      }).filter(d => d.value > 0)
    : [];

  const statusData = summary?.by_status
    ? Object.entries(summary.by_status).map(([status, amount]) => ({
        name: statusLabels[status] || status,
        value: amount
      }))
    : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Rapports</h1>
          <p className="page-description">Analyse et export des dépenses</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtres</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-wrap gap-3 items-center">
              <Label className="text-sm text-muted-foreground shrink-0">Mois NDF :</Label>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="h-9 w-36" data-testid="month-filter-month">
                  <SelectValue placeholder="Mois" />
                </SelectTrigger>
                <SelectContent>
                  {["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"].map((nom, i) => (
                    <SelectItem key={i+1} value={String(i+1).padStart(2,"0")}>{nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterYear} onValueChange={setFilterYear}>
                <SelectTrigger className="h-9 w-24" data-testid="month-filter-year">
                  <SelectValue placeholder="Année" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(filterMonth || filterYear) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground h-8 px-2"
                  onClick={() => { setFilterMonth(""); setFilterYear(""); }}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {!hasMonthFilter && (
              <div className="flex flex-wrap gap-3 items-center">
                <span className="w-px h-4 bg-border" />
                <Label className="text-sm text-muted-foreground shrink-0">Période :</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("justify-start text-left font-normal", !dateFrom && "text-muted-foreground")}
                      data-testid="date-from"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateFrom ? format(dateFrom, "d MMM yyyy", { locale: fr }) : "Date de début"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground text-sm">→</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn("justify-start text-left font-normal", !dateTo && "text-muted-foreground")}
                      data-testid="date-to"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateTo ? format(dateTo, "d MMM yyyy", { locale: fr }) : "Date de fin"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateTo} onSelect={setDateTo} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {isManager && users.length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  Collaborateur
                </Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="w-[220px]" data-testid="filter-user">
                    <SelectValue placeholder="Tous les collaborateurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les collaborateurs</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="stat-card" data-testid="stat-total">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total des dépenses</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <>
                <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {formatCurrency(summary?.total_amount)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.total_expenses || 0} dépenses
                </p>
              </>
            )}
          </CardContent>
        </Card>
        
        <Card className="stat-card" data-testid="stat-vat">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">TVA récupérable</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {formatCurrency(summary?.total_vat)}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="stat-card" data-testid="stat-pending">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">En attente</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary?.pending_approval || 0}
              </div>
            )}
          </CardContent>
        </Card>
        
        <Card className="stat-card" data-testid="stat-reimburse">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">À rembourser</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="text-3xl font-bold tabular-nums" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {summary?.pending_reimbursement || 0}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Monthly Evolution */}
        <Card data-testid="monthly-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Évolution mensuelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3, 31.8%, 91.4%)" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(215.4, 16.3%, 46.9%)" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(215.4, 16.3%, 46.9%)" />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: 'hsl(0, 0%, 100%)', 
                      border: '1px solid hsl(214.3, 31.8%, 91.4%)',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="montant" fill="hsl(222, 47%, 11%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Distribution */}
        <Card data-testid="category-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              Répartition par catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : categoryData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <RechartsPie>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(0, 0%, 100%)', 
                        border: '1px solid hsl(214.3, 31.8%, 91.4%)',
                        borderRadius: '6px'
                      }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 mt-4 justify-center">
                  {categoryData.map((item, index) => (
                    <div key={item.name} className="flex items-center gap-2 text-xs">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-muted-foreground">{item.name}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                Aucune donnée
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Export Section */}
      <Card data-testid="export-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Export comptable
          </CardTitle>
          <CardDescription>
            Exportez les données pour votre logiciel de comptabilité
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Filtrer par statut</Label>
              <Select value={exportStatus} onValueChange={setExportStatus}>
                <SelectTrigger className="w-[200px]" data-testid="export-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="approved">Approuvées</SelectItem>
                  <SelectItem value="reimbursed">Remboursées</SelectItem>
                  <SelectItem value="submitted">En attente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {users.length > 0 && (
              <div className="space-y-2">
                <Label>Collaborateur</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger className="w-[220px]" data-testid="export-user">
                    <SelectValue placeholder="Tous les collaborateurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les collaborateurs</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={exporting}
              data-testid="export-csv"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export CSV
            </Button>

            <Button
              onClick={() => handleExport('excel')}
              disabled={exporting}
              data-testid="export-excel"
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export Excel
            </Button>
          </div>

          {/* Export SAGE */}
          <div className="border-t pt-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Export SAGE (FEC)
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Format FEC — écritures comptables en partie double, comptes PCG, TVA déductible
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => handleExport('sage')}
                disabled={exporting}
                data-testid="export-sage"
                className="border-dashed"
              >
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BookOpen className="h-4 w-4 mr-2" />}
                Export SAGE
              </Button>
            </div>
          </div>

          {/* Export CEGID */}
          <div className="border-t pt-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div>
                <p className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Export CEGID
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Format CSV (;) — compatible CEGID Business &amp; Y2, compte tiers séparé, dates DD/MM/YYYY
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => handleExport('cegid')}
                disabled={exporting}
                data-testid="export-cegid"
                className="border-dashed"
              >
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Building2 className="h-4 w-4 mr-2" />}
                Export CEGID
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
